import React, { useState, useMemo } from 'react';
import { HelpCircle, ArrowRight, Calendar } from 'lucide-react';
import { getAllScores, getProductEvents, getDailyLog } from '../utils/storage';
import { getToday, getDaysInMonth, getMonthName, formatDate } from '../utils/dateUtils';

export default function History() {
    const todayDate = new Date();
    const [viewYear, setViewYear] = useState(todayDate.getFullYear());
    const [viewMonth, setViewMonth] = useState(todayDate.getMonth());
    const [selectedDate, setSelectedDate] = useState(null);

    const allScores = getAllScores();
    const today = getToday();

    const prevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear(y => y - 1);
        } else {
            setViewMonth(m => m - 1);
        }
    };

    const nextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear(y => y + 1);
        } else {
            setViewMonth(m => m + 1);
        }
    };

    // Build calendar grid
    const calendarDays = useMemo(() => {
        const daysInMonth = getDaysInMonth(viewYear, viewMonth);
        const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

        const days = [];
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push({ day: null, date: null });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const date = formatDate(new Date(viewYear, viewMonth, d));
            const scoreData = allScores[date];
            days.push({
                day: d,
                date,
                level: scoreData?.overallLevel ?? null,
                isToday: date === today,
            });
        }
        return days;
    }, [viewYear, viewMonth, allScores, today]);
    const selectedDayData = selectedDate ? allScores[selectedDate] : null;
    const selectedBehaviorData = selectedDate ? getDailyLog(selectedDate) : null;
    const selectedEventsCount = selectedDate ? getProductEvents(selectedDate).length : 0;

    // Monthly summary stats
    const monthStats = useMemo(() => {
        let loggedDays = 0;
        let highSignals = 0;
        const driverCounts = {};

        for (const [date, data] of Object.entries(allScores)) {
            const d = new Date(date + 'T12:00:00');
            if (d.getFullYear() === viewYear && d.getMonth() === viewMonth) {
                loggedDays++;
                if (data.overallLevel === 'Elevated Signal') highSignals++;

                if (data.mainDrivers) {
                    data.mainDrivers.forEach(drv => {
                        const name = drv.split('(')[0].trim();
                        driverCounts[name] = (driverCounts[name] || 0) + 1;
                    });
                }
            }
        }

        const sortedDrivers = Object.entries(driverCounts).sort((a, b) => b[1] - a[1]);
        const topMonthlyDriver = sortedDrivers.length > 0 ? sortedDrivers[0][0] : null;

        return { loggedDays, highSignals, topMonthlyDriver };
    }, [viewYear, viewMonth, allScores]);

    return (
        <div className="page" style={{ paddingBottom: 60 }}>
            <div className="page-header">
                <h1 className="page-title">History</h1>
                <p className="page-subtitle">Exposure patterns over time</p>
            </div>

            {/* Calendar nav */}
            <div className="calendar-nav">
                <button className="calendar-arrow" onClick={prevMonth}>←</button>
                <span className="calendar-month">{getMonthName(viewMonth)} {viewYear}</span>
                <button className="calendar-arrow" onClick={nextMonth}>→</button>
            </div>

            {/* Calendar grid */}
            <div className="calendar-grid">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div key={i} className="calendar-header">{d}</div>
                ))}
                {calendarDays.map((cell, i) => {
                    let dotClass = '';
                    if (cell.level === 'Elevated Signal') dotClass = 'dot-elevated';
                    else if (cell.level === 'Moderate Signal') dotClass = 'dot-moderate';
                    else if (cell.level === 'Low Signal') dotClass = 'dot-low';
                    else if (cell.level) dotClass = 'dot-unclear';

                    return (
                        <div
                            key={i}
                            className={[
                                'calendar-cell',
                                cell.day === null ? 'empty' : '',
                                cell.isToday ? 'today' : '',
                                selectedDate === cell.date ? 'selected' : '',
                            ].join(' ')}
                            onClick={() => cell.date && setSelectedDate(cell.date === selectedDate ? null : cell.date)}
                        >
                            <span>{cell.day}</span>
                            {dotClass && <div className={`calendar-dot ${dotClass}`} />}
                        </div>
                    );
                })}
            </div>

            {/* Monthly Summary */}
            <div className="card" style={{ marginTop: 20, marginBottom: 20 }}>
                <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={14} />
                    {getMonthName(viewMonth)} Trends
                </div>

                {monthStats.loggedDays === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No logs this month.</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Days Tracked</span>
                            <span style={{ fontSize: 13, fontWeight: 700 }}>{monthStats.loggedDays}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Elevated Days</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: monthStats.highSignals > 0 ? '#ef4444' : 'inherit' }}>
                                {monthStats.highSignals}
                            </span>
                        </div>
                        {monthStats.topMonthlyDriver && (
                            <div style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 8, marginTop: 4 }}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, fontWeight: 700 }}>Most Common Factor</div>
                                <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>{monthStats.topMonthlyDriver}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Selected day details */}
            {selectedDate && (
                <div className="fade-in">
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, marginTop: 24, paddingLeft: 4 }}>
                        {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </h3>

                    {(selectedDayData || selectedBehaviorData) ? (
                        <>
                            <div className="overview-hero" style={{ padding: 16, marginBottom: 16 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                                    Exposure Level
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
                                    {selectedDayData.overallLevel || 'Legacy Numeric Score'}
                                </div>
                                {selectedDayData.overallLevel && (
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                                        Confidence: {selectedDayData.confidenceSummary || 'N/A'}
                                    </div>
                                )}
                            </div>

                            {/* Grouped driver cards (new format) */}
                            {selectedDayData.topDrivers && selectedDayData.topDrivers.length > 0 ? (
                                <div className="overview-section">
                                    <h2 className="overview-section-title" style={{ fontSize: 13 }}>Top Drivers</h2>
                                    <div className="driver-cards">
                                        {selectedDayData.topDrivers.map((driver, i) => (
                                            <div key={i} className="driver-card">
                                                <div className="driver-card-name">{driver.name}</div>
                                                <div className="driver-card-signals">
                                                    {driver.signals.map((sig, j) => (
                                                        <span key={j} className="driver-signal-tag">{sig}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : selectedDayData.mainDrivers && selectedDayData.mainDrivers.length > 0 ? (
                                <div className="overview-section">
                                    <h2 className="overview-section-title" style={{ fontSize: 13 }}>Top Drivers</h2>
                                    <ul className="driver-list">
                                        {selectedDayData.mainDrivers.map((driver, i) => (
                                            <li key={i} className="driver-item" style={{ padding: '10px 14px', fontSize: 13 }}>
                                                <ArrowRight size={12} className="driver-icon" />
                                                <span>{driver}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}

                            {selectedBehaviorData && selectedBehaviorData.sleepGood !== undefined && (
                                <div className="overview-section" style={{ marginTop: 16 }}>
                                    <h2 className="overview-section-title" style={{ fontSize: 13 }}>Daily Behaviors</h2>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {selectedBehaviorData.sleepGood === false && (
                                            <div className="chip" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                                                Poor Sleep (+15% impact)
                                            </div>
                                        )}
                                        {selectedBehaviorData.stressHigh === true && (
                                            <div className="chip" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                                                High Stress (+20% impact)
                                            </div>
                                        )}
                                        {selectedBehaviorData.exercised === true && (
                                            <div className="chip" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'none' }}>
                                                Exercise (-15% impact)
                                            </div>
                                        )}
                                        {selectedBehaviorData.sleepGood === true && selectedBehaviorData.stressHigh === false && selectedBehaviorData.exercised === false && (
                                            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No major behavioral impacts logged.</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 16 }}>
                                {selectedEventsCount} scanned item{selectedEventsCount !== 1 ? 's' : ''} logged on this day.
                            </div>
                        </>
                    ) : (
                        <div className="card" style={{ textAlign: 'center' }}>
                            <div className="empty-state-icon"><HelpCircle size={32} color="var(--text-muted)" /></div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No data logged.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
